import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import isEmail from 'validator/es/lib/isEmail';
import * as Yup from 'yup';
import { ApiError, ApiSystemError } from 'api/ApiClient';
import { Authentication } from 'api/Authentication';
import { AuthHeader, AuthMarketing, Avatar, Button, FieldWrapper, FormError, Input, LayoutSplit, Password, Text, Wrapper } from 'components';
import { AuthContainer, AuthContent, AuthContentInner, AuthContentLayout, AuthFieldset } from 'components/Auth/styles';
import Form from 'components/Form/Form';
import { theme } from 'design';
import { AuthService, DeviceService } from 'lib';
import AuthMFAVerification from 'screens/Auth/AuthMFAVerification';
import { AuthType, IAuthMFARequired, IAuthRequest } from 'types';

enum Stage {
  LOADING = 'loading',
  AUTHORIZE = 'authorize',
  LOGIN = 'login',
  MFA = 'mfa',
  ERROR = 'error',
}

function decodeJwtEmail(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return (payload.email as string) ?? '';
  } catch {
    return '';
  }
}

const validationSchema = Yup.object().shape({
  identity: Yup.string()
    .test('IsIdentity', 'Please enter a valid email address.', (identity: string) => isEmail(identity || ''))
    .required('Please enter a valid email address.'),
  password: Yup.string().required('Please enter a valid password.'),
});

const McpConnect: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get('session_id');
  const mcpServer = params.get('mcp_server')?.replace(/\/$/, '') ?? '';

  const [stage, setStage] = useState<Stage>(Stage.LOADING);
  const [userEmail, setUserEmail] = useState('');
  const [mfaRequired, setMfaRequired] = useState<IAuthMFARequired | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId || !mcpServer) {
      setStage(Stage.ERROR);
      return;
    }
    const token = AuthService.getToken();
    if (token && !AuthService.isTokenExpired(token)) {
      setUserEmail(decodeJwtEmail(token));
      setStage(Stage.AUTHORIZE);
    } else {
      setStage(Stage.LOGIN);
    }
  }, [sessionId, mcpServer]);

  const transitionToAuthorize = (email?: string) => {
    if (email) {
      setUserEmail(email);
    } else {
      const token = AuthService.getToken();
      if (token) setUserEmail(decodeJwtEmail(token));
    }
    setStage(Stage.AUTHORIZE);
  };

  const { errors, isValid, touched, handleSubmit, getFieldProps, isSubmitting, setSubmitting } = useFormik<IAuthRequest>({
    initialValues: { type: AuthType.KICKFIN, identity: '', password: '', deviceId: DeviceService.getDeviceId() },
    onSubmit: async (values) => {
      setLoginError(null);
      try {
        const response = await Authentication.login(values);
        if (response.statusCode === 201) {
          transitionToAuthorize(values.identity);
        } else if (response.statusCode === 206) {
          setMfaRequired(response.body as IAuthMFARequired);
          setStage(Stage.MFA);
        }
      } catch (error) {
        if (error instanceof ApiError && error.statusCode === '401') {
          setLoginError('Invalid email or password. Please try again.');
        } else if (error instanceof ApiSystemError) {
          setLoginError('Something went wrong on our end. Please try again later.');
        } else {
          setLoginError('Something went wrong. Please check your connection and try again.');
        }
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
    validateOnBlur: true,
    validationSchema,
  });

  const validateField = (field: 'identity' | 'password') => Boolean(touched[field] && errors[field]);

  if (stage === Stage.LOADING) return null;

  return (
    <LayoutSplit
      left={
        <AuthContainer>
          <AuthContent>
            <AuthContentInner>
              {stage === 'error' && <AuthHeader title="Connection Error" subtitle="Invalid connection request. Please start the connection flow again from your AI assistant." />}

              {stage === Stage.LOGIN && (
                <>
                  <AuthHeader data-cy="auth-header-mcp-connect" title="Connect AI Assistant" subtitle="Log in to authorize your AI assistant to access Kickfin on your behalf." />
                  <AuthContentLayout>
                    <Form onSubmit={handleSubmit}>
                      <AuthFieldset>
                        <FieldWrapper data-cy="identity-field" label="Email Address" message={validateField('identity') ? errors.identity : ''} hasError={validateField('identity')}>
                          <Input type="email" disabled={isSubmitting} placeholder="you@email.com" {...getFieldProps('identity')} />
                        </FieldWrapper>
                        <FieldWrapper data-cy="password-field" label="Password" message={validateField('password') ? errors.password : ''} hasError={validateField('password')}>
                          <Password data-cy="password-input" disabled={isSubmitting} {...getFieldProps('password')} />
                        </FieldWrapper>
                      </AuthFieldset>
                      {loginError && <FormError data-cy="form-error">{loginError}</FormError>}
                      <Button label="Log In" data-cy="submit-button" type="submit" appearance="primary" size="large" disabled={isSubmitting || !isValid} isProcessing={isSubmitting} />
                    </Form>
                  </AuthContentLayout>
                </>
              )}

              {stage === Stage.MFA && mfaRequired && (
                <AuthMFAVerification
                  mfaRequired={mfaRequired}
                  onSuccess={() => transitionToAuthorize()}
                  authHeader={<AuthHeader data-cy="auth-mfa-verify-header" title="Connect AI Assistant" subtitle="Enter the code sent to your mobile phone" onBack={() => setStage(Stage.LOGIN)} value={`xxx-xxx-${mfaRequired.phoneLast4}`} />}
                />
              )}

              {stage === Stage.AUTHORIZE && (
                <>
                  <AuthHeader data-cy="auth-header-mcp-authorize" title="Connect AI Assistant" subtitle="Allow your AI assistant to access Kickfin on your behalf. You can revoke access at any time." />
                  {userEmail && (
                    <Wrapper direction="row" alignItems="center" gap={theme.space.md}>
                      <Avatar size="small" username={userEmail} />
                      <Text size="small">{userEmail}</Text>
                    </Wrapper>
                  )}
                  <form method="POST" action={`${mcpServer}/oauth/callback-direct`}>
                    <input type="hidden" name="session_id" value={sessionId!} />
                    <input type="hidden" name="jwt" value={AuthService.getToken() ?? ''} />
                    <Button type="submit" label="Authorize" appearance="primary" size="large" />
                  </form>
                </>
              )}
            </AuthContentInner>
          </AuthContent>
        </AuthContainer>
      }
      right={<AuthMarketing />}
    />
  );
};

export default McpConnect;
